import type {
  ApiConfig,
  CashCountResponse,
  CreateFieldTicketPayload,
  FeePayoutResponse,
  MutationResponse,
  SyncMutation,
  TicketResponse,
  UpdateTicketOpsPayload,
} from "./types";

export class MutationConflictError extends Error {
  readonly response: MutationResponse;

  constructor(response: MutationResponse) {
    super(response.error || "離線操作與伺服器狀態衝突");
    this.name = "MutationConflictError";
    this.response = response;
  }
}

const LIST_TIMEOUT_MS = 90_000;
const MUTATION_TIMEOUT_MS = 60_000;

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) throw new Error(`伺服器回應 ${response.status}`);
  const data = (await response.json()) as T;
  return data;
}

async function postApi<T>(config: ApiConfig, body: unknown, timeoutMs: number): Promise<T> {
  const response = await fetch(config.endpoint, {
    method: "POST",
    redirect: "follow",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(timeoutMs),
  });
  return parseResponse<T>(response);
}

export async function fetchTickets(config: ApiConfig): Promise<TicketResponse> {
  const data = await postApi<TicketResponse>(
    config,
    { action: "list", token: config.token },
    LIST_TIMEOUT_MS,
  );
  if (!data.ok) throw new Error(data.error || "讀取票券失敗");
  return data;
}

export async function sendMutation(
  config: ApiConfig,
  mutation: SyncMutation,
): Promise<MutationResponse> {
  const data = await postApi<MutationResponse>(
    config,
    {
      action: mutation.kind === "assignment" ? "assignPerformer" : "checkIn",
      token: config.token,
      ...mutation,
    },
    MUTATION_TIMEOUT_MS,
  );
  if (!data.ok && data.code === "CONFLICT") throw new MutationConflictError(data);
  if (!data.ok) throw new Error(data.error || "核票同步失敗");
  return data;
}

export async function fetchFeePayouts(config: ApiConfig): Promise<FeePayoutResponse> {
  const data = await postApi<FeePayoutResponse>(
    config,
    { action: "listFeePayouts", token: config.token },
    LIST_TIMEOUT_MS,
  );
  if (!data.ok) throw new Error(data.error || "讀取指名費失敗");
  return data;
}

export async function saveFeePayout(
  config: ApiConfig,
  payload: { session: string; performer: string; amountPaid: number; deviceId: string },
): Promise<FeePayoutResponse> {
  const data = await postApi<FeePayoutResponse>(
    config,
    { action: "saveFeePayout", token: config.token, ...payload },
    MUTATION_TIMEOUT_MS,
  );
  if (!data.ok) throw new Error(data.error || "儲存指名費失敗");
  return data;
}

export async function fetchCashCounts(config: ApiConfig): Promise<CashCountResponse> {
  const data = await postApi<CashCountResponse>(
    config,
    { action: "listCashCounts", token: config.token },
    LIST_TIMEOUT_MS,
  );
  if (!data.ok) throw new Error(data.error || "讀取錢箱失敗");
  return data;
}

export async function saveCashCount(
  config: ApiConfig,
  payload: {
    checkpoint: string;
    counts: Record<string, number>;
    balance: number;
    income: number;
    feeSnapshot: number;
    feeLocked: boolean;
    deviceId: string;
  },
): Promise<CashCountResponse> {
  const data = await postApi<CashCountResponse>(
    config,
    { action: "saveCashCount", token: config.token, ...payload },
    MUTATION_TIMEOUT_MS,
  );
  if (!data.ok) throw new Error(data.error || "儲存錢箱失敗");
  return data;
}

export async function createFieldTicket(
  config: ApiConfig,
  payload: CreateFieldTicketPayload,
): Promise<MutationResponse> {
  const data = await postApi<MutationResponse>(
    config,
    { action: "createFieldTicket", token: config.token, ...payload },
    MUTATION_TIMEOUT_MS,
  );
  if (!data.ok) throw new Error(data.error || "新增現場票失敗");
  return data;
}

export async function addFieldTicketSeat(
  config: ApiConfig,
  payload: { ticketId: string; deviceId: string },
): Promise<MutationResponse> {
  const data = await postApi<MutationResponse>(
    config,
    { action: "addFieldTicketSeat", token: config.token, ...payload },
    MUTATION_TIMEOUT_MS,
  );
  if (!data.ok) throw new Error(data.error || "增加張數失敗");
  return data;
}

export async function updateTicketOps(
  config: ApiConfig,
  payload: UpdateTicketOpsPayload,
): Promise<MutationResponse> {
  const data = await postApi<MutationResponse>(
    config,
    { action: "updateTicketOps", token: config.token, ...payload },
    MUTATION_TIMEOUT_MS,
  );
  if (!data.ok) throw new Error(data.error || "更新票務狀態失敗");
  return data;
}
